import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiMail, FiDollarSign, FiUser, FiPhone, FiMessageSquare, FiSend, FiCheckCircle, FiGlobe } = FiIcons;

// Language translations
const translations = {
  en: {
    premiumDomain: "Premium Domain",
    forSale: "For Sale",
    secure: "Secure this memorable and brandable domain name for your business.",
    couldBePerfect: "could be perfect for your next project.",
    domain: "Domain:",
    status: "Status:",
    available: "Available",
    type: "Type:",
    premium: "Premium",
    whyChoose: "Why Choose",
    memorable: "Memorable",
    memorableDesc: "Short, catchy, and easy to remember domain name",
    brandable: "Brandable",
    brandableDesc: "Perfect for building a strong, recognizable brand",
    professional: "Professional",
    professionalDesc: "extension with high commercial value",
    submitOffer: "Submit Your Offer",
    interestedIn: "Interested in purchasing",
    fillOut: "Fill out the form below with your offer details.",
    fullName: "Full Name *",
    yourFullName: "Your full name",
    emailAddress: "Email Address *",
    yourEmail: "your@email.com",
    phoneNumber: "Phone Number",
    yourPhone: "+1 (555) 123-4567",
    company: "Company/Organization",
    yourCompany: "Your company name",
    offerAmount: "Offer Amount (USD) *",
    enterOffer: "Enter your offer amount",
    additionalMessage: "Additional Message",
    tellUs: "Tell us about your intended use for the domain, timeline, or any other relevant details...",
    submitOfferBtn: "Submit Offer",
    thankYou: "Thank You!",
    offerPrepared: "Your offer has been prepared and your email client should open shortly. We'll review your submission and get back to you within 24-48 hours.",
    submitAnother: "Submit Another Offer",
    seriousInquiries: "For serious inquiries only. All offers will be reviewed and responded to promptly.",
    contact: "Contact:",
    switchToGreek: "Ελληνικά",
    switchToEnglish: "English"
  },
  el: {
    premiumDomain: "Premium Domain",
    forSale: "Προς Πώληση",
    secure: "Εξασφαλίστε αυτό το αξιομνημόνευτο και εμπορικό όνομα τομέα για την επιχείρησή σας.",
    couldBePerfect: "θα μπορούσε να είναι τέλειο για το επόμενο έργο σας.",
    domain: "Domain:",
    status: "Κατάσταση:",
    available: "Διαθέσιμο",
    type: "Τύπος:",
    premium: "Premium",
    whyChoose: "Γιατί να Επιλέξετε το",
    memorable: "Αξιομνημόνευτο",
    memorableDesc: "Σύντομο, εύηχο και εύκολο στη απομνημόνευση όνομα τομέα",
    brandable: "Εμπορεύσιμο",
    brandableDesc: "Ιδανικό για τη δημιουργία ισχυρής, αναγνωρίσιμης επωνυμίας",
    professional: "Επαγγελματικό",
    professionalDesc: "επέκταση με υψηλή εμπορική αξία",
    submitOffer: "Υποβάλετε την Προσφορά σας",
    interestedIn: "Ενδιαφέρεστε για την αγορά του",
    fillOut: "Συμπληρώστε την παρακάτω φόρμα με τις λεπτομέρειες της προσφοράς σας.",
    fullName: "Ονοματεπώνυμο *",
    yourFullName: "Το ονοματεπώνυμό σας",
    emailAddress: "Διεύθυνση Email *",
    yourEmail: "your@email.com",
    phoneNumber: "Αριθμός Τηλεφώνου",
    yourPhone: "+30 6XX XXX XXXX",
    company: "Εταιρεία/Οργανισμός",
    yourCompany: "Το όνομα της εταιρείας σας",
    offerAmount: "Ποσό Προσφοράς (USD) *",
    enterOffer: "Εισάγετε το ποσό της προσφοράς σας",
    additionalMessage: "Πρόσθετο Μήνυμα",
    tellUs: "Πείτε μας για την προβλεπόμενη χρήση του domain, το χρονοδιάγραμμα ή οποιαδήποτε άλλη σχετική λεπτομέρεια...",
    submitOfferBtn: "Υποβολή Προσφοράς",
    thankYou: "Ευχαριστούμε!",
    offerPrepared: "Η προσφορά σας έχει προετοιμαστεί και το πρόγραμμα ηλεκτρονικής αλληλογραφίας σας θα ανοίξει σύντομα. Θα εξετάσουμε την υποβολή σας και θα επικοινωνήσουμε μαζί σας εντός 24-48 ωρών.",
    submitAnother: "Υποβολή Άλλης Προσφοράς",
    seriousInquiries: "Μόνο για σοβαρές ερωτήσεις. Όλες οι προσφορές θα εξεταστούν και θα απαντηθούν άμεσα.",
    contact: "Επικοινωνία:",
    switchToGreek: "Ελληνικά",
    switchToEnglish: "English"
  }
};

function App() {
  const [language, setLanguage] = useState('en');
  const [domainInfo, setDomainInfo] = useState({
    fullDomain: 'example.com',
    domainName: 'example',
    extension: 'com',
    contactEmail: 'info@waveloc.com' // Fixed contact email regardless of domain
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    offer: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const t = translations[language];

  useEffect(() => {
    // Get the current domain from the window location
    const currentDomain = window.location.hostname;
    
    // For local development, allow testing with URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const testDomain = urlParams.get('domain');
    
    // Use either the real domain or the test domain from URL parameter
    const domainToUse = testDomain || currentDomain;
    
    // Parse the domain
    const domainParts = domainToUse.split('.');
    const extension = domainParts.length > 1 ? domainParts.pop() : 'com';
    const subdomainParts = domainParts;
    const domainName = subdomainParts.pop() || 'example';
    
    // Set the domain information with fixed contact email
    setDomainInfo({
      fullDomain: domainToUse,
      domainName: domainName,
      extension: extension,
      contactEmail: 'info@waveloc.com' // Always use this email
    });
    
    // Update page title
    document.title = `${domainToUse} - Premium Domain For Sale`;

    // Check for preferred language in localStorage or URL
    const savedLanguage = localStorage.getItem('preferredLanguage');
    const langParam = urlParams.get('lang');
    
    if (langParam && (langParam === 'el' || langParam === 'en')) {
      setLanguage(langParam);
      localStorage.setItem('preferredLanguage', langParam);
    } else if (savedLanguage && (savedLanguage === 'el' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
    
    // Greek domains might default to Greek language
    if (extension === 'gr' && !savedLanguage && !langParam) {
      setLanguage('el');
      localStorage.setItem('preferredLanguage', 'el');
    }
  }, []);

  const handleLanguageSwitch = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`Domain Purchase Inquiry - ${domainInfo.fullDomain}`);
    const body = encodeURIComponent(`
Domain Purchase Inquiry for ${domainInfo.fullDomain}

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Company: ${formData.company}
Offer Amount: ${formData.offer}

Message:
${formData.message}

Best regards,
${formData.name}
    `);
    
    window.location.href = `mailto:info@waveloc.com?subject=${subject}&body=${body}`;
    setIsSubmitted(true);
  };

  // Generate a domain-specific color scheme
  const getDomainColors = () => {
    // Simple hash function to generate consistent colors for each domain
    const simpleHash = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash) % 360; // Convert to hue (0-359)
    };
    
    const hue = simpleHash(domainInfo.domainName);
    
    return {
      primary: `hsl(${hue}, 70%, 50%)`,
      secondary: `hsl(${(hue + 30) % 360}, 70%, 45%)`,
      light: `hsl(${hue}, 70%, 95%)`,
      dark: `hsl(${hue}, 70%, 20%)`
    };
  };
  
  const colors = getDomainColors();
  
  const getColorClasses = () => {
    // Predefined color themes based on common TLDs
    const colorMap = {
      'com': 'from-blue-50 via-white to-blue-100',
      'io': 'from-purple-50 via-white to-purple-100',
      'ai': 'from-green-50 via-white to-green-100',
      'org': 'from-amber-50 via-white to-amber-100',
      'net': 'from-cyan-50 via-white to-cyan-100',
      'co': 'from-indigo-50 via-white to-indigo-100',
      'dev': 'from-rose-50 via-white to-rose-100',
      'app': 'from-emerald-50 via-white to-emerald-100',
      'gr': 'from-blue-100 via-white to-blue-200'
    };
    
    return colorMap[domainInfo.extension] || 'from-blue-50 via-white to-blue-100';
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${getColorClasses()}`} style={{
      '--primary-color': colors.primary,
      '--secondary-color': colors.secondary,
      '--light-color': colors.light,
      '--dark-color': colors.dark,
    }}>
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-10">
        <div className="flex gap-2">
          <button 
            onClick={() => handleLanguageSwitch('en')}
            className={`px-3 py-1 text-sm rounded-md transition-all ${language === 'en' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white/50 text-gray-700 hover:bg-white/80'}`}
          >
            EN
          </button>
          <button 
            onClick={() => handleLanguageSwitch('el')}
            className={`px-3 py-1 text-sm rounded-md transition-all ${language === 'el' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white/50 text-gray-700 hover:bg-white/80'}`}
          >
            EL
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="pt-8 pb-4">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <h1 className="text-5xl font-bold mb-2" style={{ color: colors.primary }}>
                {domainInfo.domainName}
                <span className="opacity-80">.{domainInfo.extension}</span>
              </h1>
              <div className="w-24 h-1 mx-auto rounded-full" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}></div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-6xl font-bold text-gray-800 mb-6">
              {t.premiumDomain}
              <span className="block" style={{ color: colors.primary }}>{t.forSale}</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t.secure}
              <strong style={{ color: colors.primary }}> {domainInfo.fullDomain}</strong> {t.couldBePerfect}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                <span className="text-sm text-gray-500">{t.domain}</span>
                <span className="text-lg font-bold ml-2" style={{ color: colors.primary }}>{domainInfo.fullDomain}</span>
              </div>
              <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                <span className="text-sm text-gray-500">{t.status}</span>
                <span className="text-lg font-bold text-green-600 ml-2">{t.available}</span>
              </div>
              <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                <span className="text-sm text-gray-500">{t.type}</span>
                <span className="text-lg font-bold text-gray-700 ml-2">{t.premium}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">
              {t.whyChoose} <span style={{ color: colors.primary }}>{domainInfo.fullDomain}</span>?
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" 
                     style={{ backgroundColor: colors.light }}>
                  <SafeIcon icon={FiCheckCircle} className="text-2xl" style={{ color: colors.primary }} />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{t.memorable}</h4>
                <p className="text-gray-600">{t.memorableDesc}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                     style={{ backgroundColor: colors.light }}>
                  <SafeIcon icon={FiDollarSign} className="text-2xl" style={{ color: colors.primary }} />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{t.brandable}</h4>
                <p className="text-gray-600">{t.brandableDesc}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                     style={{ backgroundColor: colors.light }}>
                  <SafeIcon icon={FiGlobe} className="text-2xl" style={{ color: colors.primary }} />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{t.professional}</h4>
                <p className="text-gray-600">.{domainInfo.extension} {t.professionalDesc}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                {t.submitOffer}
              </h3>
              <p className="text-lg text-gray-600">
                {t.interestedIn} {domainInfo.fullDomain}? {t.fillOut}
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.fullName}
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                        style={{ '--tw-ring-color': colors.primary }}
                        placeholder={t.yourFullName}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.emailAddress}
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                        style={{ '--tw-ring-color': colors.primary }}
                        placeholder={t.yourEmail}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.phoneNumber}
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiPhone} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                        style={{ '--tw-ring-color': colors.primary }}
                        placeholder={t.yourPhone}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.company}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                      style={{ '--tw-ring-color': colors.primary }}
                      placeholder={t.yourCompany}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.offerAmount}
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      name="offer"
                      value={formData.offer}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                      style={{ '--tw-ring-color': colors.primary }}
                      placeholder={t.enterOffer}
                    />
                  </div>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.additionalMessage}
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all resize-none"
                      style={{ '--tw-ring-color': colors.primary }}
                      placeholder={t.tellUs}
                    ></textarea>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
                  style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}
                >
                  <SafeIcon icon={FiSend} className="text-lg" />
                  {t.submitOfferBtn}
                </motion.button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl shadow-xl p-8 text-center"
              >
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" 
                     style={{ backgroundColor: 'rgba(74, 222, 128, 0.2)' }}>
                  <SafeIcon icon={FiCheckCircle} className="text-3xl text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  {t.thankYou}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t.offerPrepared}
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="font-medium hover:underline"
                  style={{ color: colors.primary }}
                >
                  {t.submitAnother}
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-800 text-white">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 mb-2">
            {t.seriousInquiries}
          </p>
          <p className="text-sm text-gray-500">
            {t.contact} <a href="mailto:info@waveloc.com" className="hover:underline" style={{ color: colors.primary }}>info@waveloc.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;